import { QualityScore } from "../../types/api";
import i18next from 'i18next';

/**
 * Calculate the overall score from individual quality metrics
 * @param scores Individual quality metric scores
 * @returns Average of all scores rounded to nearest integer
 */
export const calculateOverallScore = (scores: {
  category_placement_score?: number;
  desc_html_score?: number;
  alt_text_score?: number;
  images_count_score?: number;
  title_score?: number;
  seo_description_score?: number;
  tags_score?: number;
  seo_title_score?: number;
}): number => {
  return Math.round(
    (
      (scores.category_placement_score ?? 0) +
      (scores.desc_html_score ?? 0) +
      (scores.alt_text_score ?? 0) +
      (scores.images_count_score ?? 0) +
      (scores.title_score ?? 0) +
      (scores.seo_description_score ?? 0) +
      (scores.tags_score ?? 0) +
      (scores.seo_title_score ?? 0)
    ) / 8
  );
};

/**
 * Get quality message based on title score
 */
export const getTitleQualityMessage = (score: number): string => {
  if (score === 0) return i18next.t('qualityMessages.title_none');
  if (score < 40) return i18next.t('qualityMessages.title_poor');
  if (score < 70) return i18next.t('qualityMessages.title_good');
  return i18next.t('qualityMessages.title_excellent');
};

/**
 * Get quality message based on description score
 */
export const getDescriptionQualityMessage = (score: number): string => {
  if (score === 0) return i18next.t('qualityMessages.description_none');
  if (score < 40) return i18next.t('qualityMessages.description_poor');
  if (score < 70) return i18next.t('qualityMessages.description_good');
  return i18next.t('qualityMessages.description_excellent');
};

/**
 * Get quality message based on description bullets score
 */
export const getDescriptionBulletsQualityMessage = (score: number): string => {
  if (score === 0) return i18next.t('qualityMessages.bullets_none');
  if (score < 40) return i18next.t('qualityMessages.bullets_poor');
  if (score < 70) return i18next.t('qualityMessages.bullets_good');
  return i18next.t('qualityMessages.bullets_excellent');
};

/**
 * Get quality message based on image score
 */
export const getImageQualityMessage = (score: number): string => {
  if (score === 0) return i18next.t('qualityMessages.images_none');
  if (score < 40) return i18next.t('qualityMessages.images_poor');
  if (score < 70) return i18next.t('qualityMessages.images_good');
  return i18next.t('qualityMessages.images_excellent');
};

/**
 * Get quality message based on SEO score
 */
export const getSeoQualityMessage = (score: number): string => {
  if (score === 0) return i18next.t('qualityMessages.seo_none');
  if (score < 40) return i18next.t('qualityMessages.seo_poor');
  if (score < 70) return i18next.t('qualityMessages.seo_good');
  return i18next.t('qualityMessages.seo_excellent');
};

/**
 * Get quality message based on alt tags score
 */
export const getAltTagsQualityMessage = (score: number): string => {
  if (score === 0) return i18next.t('qualityMessages.alt_tags_none');
  if (score < 40) return i18next.t('qualityMessages.alt_tags_poor');
  if (score < 70) return i18next.t('qualityMessages.alt_tags_good');
  return i18next.t('qualityMessages.alt_tags_excellent');
};

/**
 * Get quality message based on category score
 */
export const getCategoryQualityMessage = (score: number): string => {
  if (score === 0) return i18next.t('qualityMessages.categories_none');
  if (score < 40) return i18next.t('qualityMessages.categories_poor');
  if (score < 70) return i18next.t('qualityMessages.categories_good');
  return i18next.t('qualityMessages.categories_excellent');
};

/**
 * Get quality message based on product tags score
 */
export const getProductTagsQualityMessage = (score: number): string => {
  if (score === 0) return i18next.t('qualityMessages.tags_none');
  if (score < 40) return i18next.t('qualityMessages.tags_poor');
  if (score < 70) return i18next.t('qualityMessages.tags_good');
  return i18next.t('qualityMessages.tags_excellent');
};

/**
 * Extract and round all quality scores from a QualityScore object
 */
interface QualityScores {
  titleQualityScore: number;
  descriptionQualityScore: number;
  imageQualityScore: number;
  seoQualityScore: number;
  altTagsQualityScore: number;
  categoryQualityScore: number;
  tagsScore: number;
  descriptionBulletsQualityScore: number;
}

export const extractQualityScores = (score: QualityScore): QualityScores => ({
  titleQualityScore: Math.round(score.total_title_score ?? 0),
  descriptionQualityScore: Math.round(score.total_desc_html_score ?? 0),
  imageQualityScore: Math.round(score.total_images_count_score ?? 0),
  seoQualityScore: Math.round(score.total_seo_description_score ?? 0),
  altTagsQualityScore: Math.round(score.total_alt_text_score ?? 0),
  categoryQualityScore: Math.round(score.total_category_placement_score ?? 0),
  tagsScore: Math.round(score.total_tags_score ?? 0),
  descriptionBulletsQualityScore: Math.round(score.total_description_bullets_score ?? 0)
});

/**
 * Get estimated impact percentage based on average quality score
 * @param averageScore The average quality score
 * @returns Estimated impact range as a string percentage
 */
export const getEstimatedImpact = (averageScore: number): string | null => {
  if (averageScore < 30) {
    return i18next.t('qualityMessages.impact_high');
  } else if (averageScore < 70) {
    return i18next.t('qualityMessages.impact_medium');
  } else if (averageScore < 90) {
    return i18next.t('qualityMessages.impact_low');
  }
  return null;
};

/**
 * Get overall quality message based on average score
 * @param averageScore The average quality score
 * @returns Quality message describing overall content status
 */
export const getQualityMessage = (averageScore: number): string => {
  if (averageScore === 0) {
    return i18next.t('qualityMessages.overall_none');
  } else if (averageScore < 40) {
    return i18next.t('qualityMessages.overall_poor');
  } else if (averageScore < 70) {
    return i18next.t('qualityMessages.overall_good');
  } else {
    return i18next.t('qualityMessages.overall_excellent');
  }
};

/**
 * Calculate average quality score from individual scores
 * @param scores Individual quality scores
 * @param seoTitleScore Optional SEO title score
 * @returns Rounded average score
 */
export const calculateAverageQualityScore = (scores: QualityScores, seoTitleScore?: number): number => {
  const qualitySum = Object.values(scores).reduce((sum, score) => sum + score, 0) + 
    (seoTitleScore ?? 0);
  return Math.round(qualitySum / 8);
};
